import { FC } from 'react';

import {
	BUTTON_TYPES,
	EvBodyMedium,
	EvButton,
	EvFlexColumn,
	EvFlexRow,
	EvInstructions,
	EvLabelDefault,
	EvSection,
	EvSvgIcon,
	EvTextInput,
	EvTitle,
	Info,
	OptionType,
	TITLE_MODES
} from '@evinced-private/ui-common';

import { IWfaLabel } from 'src/apps/wfa/services/WfaDashboardServiceTypes';

import { IEditSessionForm } from './EditSessionForm.types';
import { ARIA_LABEL_ID } from './EditSessionPopup.constants';
import { EDIT_SESSION_POPUP_LABELS } from './EditSessionPopup.labels';
import { isCoreLabel } from './EditSessionPopup.utils';
import { LabelsSelect } from './LabelsSelect';

const transformOptions = (options: { key: string; name: string }[]): OptionType[] => {
	return options.map(({ key, name }) => ({
		label: name,
		value: key
	}));
};

export const EditSessionForm: FC<IEditSessionForm> = ({
	name,
	onNameChange,
	showInfoMessage,
	onDismissMessage,
	isLabelsToggleEnabled,
	availableLabels,
	selectedLabels,
	onLabelChange
}) => (
	<EvFlexColumn gap="LARGE" padding={['NONE', 'MEDIUM']}>
		{showInfoMessage && (
			<div className="info-message-container">
				<EvFlexRow gap="MEDIUM" align="CENTER" width="auto">
					<EvSvgIcon icon={Info} isSmall />
					<EvBodyMedium>{EDIT_SESSION_POPUP_LABELS.DISMISS_MESSAGE}</EvBodyMedium>
				</EvFlexRow>
				<EvButton type={BUTTON_TYPES.GHOST} onClick={onDismissMessage}>
					{EDIT_SESSION_POPUP_LABELS.DISMISS_BUTTON}
				</EvButton>
			</div>
		)}

		<EvFlexColumn gap="SMALL">
			<EvLabelDefault as="label" id={ARIA_LABEL_ID}>
				{EDIT_SESSION_POPUP_LABELS.SESSION_NAME}
			</EvLabelDefault>
			<EvTextInput
				ariaLabelledBy={ARIA_LABEL_ID}
				value={name}
				onChange={onNameChange}
				maxLength="255"
				isRequired
			/>
		</EvFlexColumn>

		{isLabelsToggleEnabled && (
			<EvSection>
				<EvFlexColumn gap="LARGE">
					<EvFlexColumn gap="SMALL">
						<EvTitle
							mode={TITLE_MODES.NORMAL}
							headingLevel={3}
							titleText={EDIT_SESSION_POPUP_LABELS.PLATFORM_LABELS_TITLE}
							id="platform-labels"
						/>
						<EvInstructions isSmallText text={EDIT_SESSION_POPUP_LABELS.INSTRUCTIONS} />
					</EvFlexColumn>

					<EvFlexColumn gap="LARGE">
						{availableLabels.map((item) => {
							const placeholder = EDIT_SESSION_POPUP_LABELS.getSelectLabelPlaceholder(item.name);
							const selectedLabel: IWfaLabel | undefined = selectedLabels?.find(
								(label) => label.key === item.key
							);
							const value: OptionType[] =
								selectedLabel?.values?.map((selectedValue) => ({
									label: selectedValue?.name,
									value: selectedValue?.key
								})) ?? [];

							return (
								<LabelsSelect
									key={item.key}
									labelKey={item.key}
									options={transformOptions(item.options)}
									value={value}
									onChange={(selected) => onLabelChange(item, selected)}
									isMulti={!isCoreLabel(item.key)}
									description={item.description}
									placeholder={placeholder}
									ariaLabel={item.name}
								/>
							);
						})}
					</EvFlexColumn>
				</EvFlexColumn>
			</EvSection>
		)}
	</EvFlexColumn>
);
